import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Combobox } from '@vwfs-bronson/bronson-react';
import { useFormikContext } from 'formik';
import { FormField } from '../../components/FormField/FormField';
import { DealerDashboardSubpage } from '../../resources/base/models/enums';
import { deduplicateObjectsArray } from '../../services/common/miscUtils';
import { staticSelector } from '../../services/redux';
import { dashboardSelectors } from '../../services/redux/features/dashboard.slice';
import {
  defaultSelectedStatusIdsBySubpageMap,
  getStatusIdsByLabel,
  groupedStatusIdsBySubpageMap,
  offerStatusIdOptionsList,
  OfferStatusIdType,
} from './constants/StatusMap';

interface StatusIdFilterSelectionFieldProps {
  name: string;
  label?: any;
  required?: boolean;
  subpage: DealerDashboardSubpage;
}

interface CustomOption {
  textValue: string;
  content: React.ReactNode;
}

interface ComboboxOption {
  key: string;
  value: string | CustomOption;
  disabled?: boolean;
}

const getStatusIdsFromOptions = (options: ComboboxOption[]) => {
  return options.map(({ value: label }) => getStatusIdsByLabel(label as string)).flat();
};

const getInitialStatuses = (subpage: DealerDashboardSubpage, savedValue?: string): OfferStatusIdType[] => {
  const selectedStatusIds =
    staticSelector(dashboardSelectors.selectSubPageLabelStatusIds) || (savedValue?.split(',') as OfferStatusIdType[]);
  const groupedStatusIdsForSubpage = groupedStatusIdsBySubpageMap[subpage];

  if (selectedStatusIds?.length) return selectedStatusIds;

  const allStatuses = groupedStatusIdsForSubpage.map(({ value }) => value).flat();

  // Use the subpage's default selection when defined (e.g. Summary excludes 'Annulé', and
  // both Summary and Studies exclude 'Expiré'); otherwise default to all statuses.
  return defaultSelectedStatusIdsBySubpageMap[subpage] ?? allStatuses;
};

const convertStatusesToComboboxOptions = (statuses: OfferStatusIdType[]) => {
  return deduplicateObjectsArray(
    statuses?.map((statusId) => ({
      key: offerStatusIdOptionsList[statusId]?.label,
      value: offerStatusIdOptionsList[statusId]?.label,
    })),
    (option) => option?.key
  );
};

const selectAllOption: ComboboxOption = {
  key: 'Tout sélectionner',
  value: 'Tout sélectionner',
};

export const isSelectAllChecked = (options: ComboboxOption[]) => {
  return !!options.find(({ key }) => key === selectAllOption.key);
};

export const StatusFilterCombobox: FunctionComponent<StatusIdFilterSelectionFieldProps> = ({
  name,
  label,
  required,
  subpage,
}) => {
  const { setFieldValue } = useFormikContext<any>();
  const searchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);

  const [options] = useState(() => [
    selectAllOption,
    ...convertStatusesToComboboxOptions(groupedStatusIdsBySubpageMap[subpage].map(({ value }) => value).flat()),
  ]);
  const [initialSelectedStatuses] = useState(getInitialStatuses(subpage, searchForm.statusId as OfferStatusIdType));
  const [selectedOptions, setSelectedOptions] = useState<ComboboxOption[]>(() => {
    const statusOptions = convertStatusesToComboboxOptions(initialSelectedStatuses);
    const groupedStatusIdsBySubpage = groupedStatusIdsBySubpageMap[subpage].map(({ value }) => value).flat();
    const allSelected = initialSelectedStatuses.length === groupedStatusIdsBySubpage.length;

    return allSelected ? [selectAllOption, ...statusOptions] : statusOptions;
  });

  const [selectAllIsChecked, setSelectAllIsChecked] = useState<boolean>(isSelectAllChecked(selectedOptions));

  useEffect(() => {
    setFieldValue(name, initialSelectedStatuses.join(','));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <FormField
      required={required}
      type="combobox"
      name={name}
      labelText={label}
      render={(fieldProps) => (
        <Combobox
          {...fieldProps}
          name={name}
          multiple
          options={options}
          valueFormatter={(_selectedOptions) => {
            return _selectedOptions
              .filter(({ key }) => key !== selectAllOption.key)
              .map(({ value }) => value)
              .join(', ');
          }}
          value={selectedOptions}
          onBlur={() => {
            if (selectedOptions.length === 0) {
              setSelectedOptions([...options]);
            }
          }}
          onChange={(newSelectedOptions) => {
            const isSelectAllCurrentlyChecked = isSelectAllChecked(newSelectedOptions);
            const selectAllWasChanged = selectAllIsChecked !== isSelectAllCurrentlyChecked;
            let _newSelectedOptions: ComboboxOption[];

            if (selectAllWasChanged) {
              if (isSelectAllCurrentlyChecked) {
                _newSelectedOptions = [...options];
              } else {
                _newSelectedOptions = [];
              }
            } else {
              _newSelectedOptions = newSelectedOptions.filter(({ value }) => value !== selectAllOption.key);

              if (_newSelectedOptions.length === options.length - 1) {
                _newSelectedOptions.unshift(selectAllOption);
              }
            }

            setSelectAllIsChecked(isSelectAllChecked(_newSelectedOptions));
            setSelectedOptions(_newSelectedOptions);
            setFieldValue(name, getStatusIdsFromOptions(_newSelectedOptions).join(','));
          }}
        />
      )}
    />
  );
};
